import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Dropdown } from 'yankees-react-utils';
import { HeatMaps, useOktaQuery } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import StrikeZone from '../../../components/StrikeZone';
import { base64ToBlob } from '../../../utils/heatmaps';
import usePlayerSelector from '../../../utils/usePlayerSelector';

const Wrapper = styled.div`
`;
const StyledLegendBadge = styled.i`
    display: inline-block;
    line-height: 1em;
    max-height: 1em;
    width: 3em;
    min-width: 3em;
    margin-right: 0.5em;
    background-color: ${(props) => props.color};
`;

const HitterStrengthsDamageZones = ({ playerid }) => {
    const { t: translate } = useTranslation();
    const yearid = -2;
    const [isLHP, setIsLHP] = useState(false);
    const Player = usePlayerSelector();

    const { data: heatmaps, isFetching: loadingHeatmaps } = useOktaQuery(
        ['BatterSwingMapsByPitchType', { playerid, yearid }],
        (t) => HeatMaps.getBatterSwingMapsByPitchType(t, playerid, { yearid }),
    );

    const splits = [
        { name: translate('sections.reports.hitter.attack-count'), value: 'Early' },
        { name: translate('sections.reports.hitter.two-strike'), value: 'TwoStrikes' },
    ];
    const subtypes = [
        { name: '', value: 'All' },
        { name: translate('baseball.pitches.fastball_other'), value: 'fb' },
        { name: translate('baseball.pitches.changeup_other'), value: 'ch' },
        { name: translate('baseball.pitches.breaking-ball_other'), value: 'bb' },
    ];
    const handednessDropdownOptions = [{ name: translate('baseball.rhp'), value: 1 }, { name: translate('baseball.lhp'), value: 2 }];

    const loading = /* loadingDamageZones || */ loadingHeatmaps;

    return (
        <Wrapper>
            <div className="d-flex justify-content-between">
                <div>
                    <h4>{translate('sections.reports.hitter.damage-zones')}</h4>
                </div>
                <div>
                    <div className="d-flex">
                        <div style={{ lineHeight: '3em' }}>{translate('sections.reports.hitter.pitcher-hand')}:</div>
                        <div>
                            <Dropdown
                                align="end"
                                className="ms-2"
                                options={handednessDropdownOptions}
                                value={isLHP ? translate('baseball.lhp') : translate('baseball.rhp')}
                                onSelect={({ value }) => setIsLHP(value === 2)}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ReportCard>
                {loading && <LoadingSpinner />}
                {!loading && (
                    <>
                        <div className="d-flex flex-row justify-content-center mb-1">
                            <div className="p-2"><b>{translate('common.key')}:</b></div>
                            <div className="p-2">
                                <StyledLegendBadge color="red">&nbsp;</StyledLegendBadge>
                                {translate('sections.reports.hitter.good-results-swings')}
                            </div>
                            <div className="p-2"><b>K:</b> {translate('sections.reports.hitter.swinging-strikeout')}</div>
                            <div className="p-2"><b>{translate('sections.reports.hitter.diamond')}:</b> {translate('baseball.abbr-homerun')}</div>
                            <div className="p-2"><b>{translate('sections.reports.hitter.solid-curve')}:</b> {translate('sections.reports.hitter.hitter-swings-50')}</div>
                        </div>
                        {splits.map((split) => (
                            <div key={`Split_${split.value}`} className="mb-2">
                                <div className="d-flex flex-wrap flex-row">
                                    {subtypes.map((subtype) => {
                                        const heatmap = heatmaps?.find((x) => x.IsLHP === isLHP
                                            && x.Split === split.value && x.SubType === subtype.value);
                                        return (
                                            <div className="p-2 flex-fill" key={`Subtype_${subtype.value}`}>
                                                <h5>{split.name} {subtype.name}</h5>
                                                {heatmap && (
                                                    <StrikeZone
                                                        enableZoom={false}
                                                        seriesData={[]}
                                                        size="sm"
                                                        yopposite
                                                        showlhb={Player.Bats === 2 || (Player.Bats === 3 && !isLHP)}
                                                        showrhb={Player.Bats === 1 || (Player.Bats === 3 && isLHP)}
                                                        heatMapBackground={base64ToBlob(heatmap)}
                                                        hideGrid
                                                        hideAxis
                                                        showHeatMapBackground
                                                        pitcherView={false}
                                                        sztop={Player.PlayerStrikeZoneABS?.StrikezoneAbsTop}
                                                        szbottom={Player.PlayerStrikeZoneABS?.StrikezoneAbsBottom}
                                                        // showStrikeZone={false}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </ReportCard>
        </Wrapper>
    );
};

HitterStrengthsDamageZones.propTypes = {
    playerid: PropTypes.number.isRequired,
};

export default HitterStrengthsDamageZones;
